import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { BechAccount, CardAccount, SelfServiceableAccountsPayload } from '../models/accounts.model';
import { Router } from '@angular/router';
import { EventTrackingService } from 'src/app/services/event-tracking.service';
import { WindowRefService } from '../../../services/window-ref.service';
import { MatDialog } from '@angular/material/dialog';
import { LOAN_CPID } from '../../../config/account.config';
import { ComponentType } from '@angular/cdk/portal';
import { LinkClick, NavigateToCsw } from 'src/app/models/event-data.model';
import { AuthEventType } from 'src/app/enums/event/event.enum';
import { AuthLinkType } from 'src/app/enums/event/event-data.enum';
import { getAccountDirectLink } from '../util/accounts.util';
import { RedirectAccount } from '../../redirect/models/self-serviceable-accounts.model';
import { LoanAccountSlim } from '../../../containers/header-nav/header-nav.model';
import { AccountClosedModalComponent } from '../components/account-closed-modal/account-closed-modal.component';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  constructor(
    private readonly cookieService: CookieService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly eventTrackingService: EventTrackingService,
    private readonly windowRefService: WindowRefService,
    private readonly dialog: MatDialog
  ) {}

  getSelfServicableAccounts() {
    return this.http.get<SelfServiceableAccountsPayload>(`${environment.customerGateway}/accounts`, {
      headers: { Authorization: this.cookieService.get('X-Access-Token') },
    });
  }

  getIsClosedAccount(loanAccount: LoanAccountSlim) {
    const reposessionStatus = loanAccount.repossessionCaseStatus && loanAccount.repossessionCaseStatus.toLowerCase();
    return (
      loanAccount.accountStatus === 'Closed' || (reposessionStatus === 'repossessed' && !loanAccount.reinstatedDate)
    );
  }

  navigateToPersonalLoan(loanAccount: LoanAccountSlim) {
    if (this.getIsClosedAccount(loanAccount)) {
      return this.dialog.open(AccountClosedModalComponent, {
        maxWidth: '569px',
        data: {
          productType: loanAccount.productType,
          last4DigitsExternalAccountNumber: loanAccount.last4DigitsExternalAccountNumber,
        },
      });
    }

    if (loanAccount.url.includes(LOAN_CPID)) {
      this.eventTrackingService.trackEnterpriseEvent({
        authClickLinkType: 'clickNavigationForLoanApplication',
        cid: this.cookieService.get('cid'),
        eventName: 'linkClick',
        authClickLinkText: 'Personal Loan',
        cpid: LOAN_CPID,
      });
      window.open(loanAccount.url, '_blank');
      return;
    } else {
      this.windowRefService.nativeWindow.FS.event('[Auth] Personal Loan Login', {
        cid: this.cookieService.get('cid'),
        authSessionId: sessionStorage.getItem('auth-session-id'),
        accountId: loanAccount.id,
        destination: 'dashboard',
      });
      Promise.all([
        this.eventTrackingService.trackEnterpriseEvent({
          eventName: 'redirectToLoanSite',
          cid: this.cookieService.get('cid'),
        }),
        // don't fire eev when coming from loan redirect comp since the id will be null
        loanAccount.id
          ? this.eventTrackingService.trackEvent<NavigateToCsw>(AuthEventType.NAVIGATE_TO_CSW, {
              cid: this.cookieService.get('cid'),
              authSessionId: sessionStorage.getItem('auth-session-id'),
              loanAccountNumber: loanAccount.id.toString(),
            })
          : Promise.resolve(),
      ]).finally(() => {
        this.windowRefService.nativeWindow.location.href = loanAccount.url;
      });
    }
  }

  directLinkToPersonalLoan(loanAccount: LoanAccountSlim) {
    let redirectLink = loanAccount.url;
    if (loanAccount.id) {
      redirectLink = getAccountDirectLink(loanAccount.url, 'dashboard', loanAccount.id);
    }

    this.navigateToPersonalLoan({
      url: redirectLink,
      productType: loanAccount.productType,
      accountStatus: loanAccount.accountStatus,
      last4DigitsExternalAccountNumber: loanAccount.last4DigitsExternalAccountNumber,
      id: loanAccount.id,
      repossessionCaseStatus: loanAccount.repossessionCaseStatus,
      reinstatedDate: loanAccount.reinstatedDate,
    });
  }

  navigateToPersonalLoanChangeDueDate(url: string, accountId: string) {
    const changeDueDateUrl = `${url}&destination=changeduedate&id=${accountId?.trim()}`;
    this.windowRefService.nativeWindow.FS.event('[Auth] Personal Loan Login', {
      cid: this.cookieService.get('cid'),
      authSessionId: sessionStorage.getItem('auth-session-id'),
      accountId: accountId,
      destination: 'change due date',
    });

    this.eventTrackingService
      .trackEvent<NavigateToCsw>(AuthEventType.NAVIGATE_TO_CSW, {
        cid: this.cookieService.get('cid'),
        authSessionId: sessionStorage.getItem('auth-session-id'),
        loanAccountNumber: accountId,
      })
      .finally(() => {
        this.windowRefService.nativeWindow.location.href = changeDueDateUrl;
      });
  }

  navigateToPersonalLoanPayment(loanAccount: LoanAccountSlim) {
    if (this.getIsClosedAccount(loanAccount)) {
      return this.dialog.open(AccountClosedModalComponent, {
        maxWidth: '569px',
        data: {
          productType: loanAccount.productType,
          last4DigitsExternalAccountNumber: loanAccount.last4DigitsExternalAccountNumber,
        },
      });
    }
    // TODO: verify FS events for makepayment redirect
    // this.windowRefService.nativeWindow.FS.event('[Auth] Personal Loan Login');
    const paymentUrl = `${loanAccount.url}&destination=makepayment&id=${loanAccount.id?.toString().trim()}`;
    this.windowRefService.nativeWindow.FS.event('[Auth] Personal Loan Login', {
      cid: this.cookieService.get('cid'),
      authSessionId: sessionStorage.getItem('auth-session-id'),
      accountId: loanAccount.id,
      destination: 'make a payment',
    });
    Promise.all([
      this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, {
        authClickLinkText: 'Make a payment',
        authClickLinkType: AuthLinkType.NAV_TO_LOAN_PAYMENT,
      }),
      this.eventTrackingService.trackEvent<NavigateToCsw>(AuthEventType.NAVIGATE_TO_CSW, {
        cid: this.cookieService.get('cid'),
        authSessionId: sessionStorage.getItem('auth-session-id'),
        loanAccountNumber: loanAccount.id.toString(),
      }),
    ]).finally(() => {
      this.windowRefService.nativeWindow.location.href = paymentUrl;
    });
  }

  navigateToCreditHealth(url: string, kbaCompleted: boolean, isCta = false) {
    this.windowRefService.nativeWindow.FS.event('[Auth] Credit Health Login');
    this.eventTrackingService
      .trackEnterpriseEvent({
        eventName: 'redirectToCreditHealthSite',
        cid: this.cookieService.get('cid'),
      })
      .finally(() => {
        // events when cta button clicked
        if (isCta) {
          const CTA_EVENT = kbaCompleted
            ? '[Auth] Customer Hub BEFH Credit Score Review click'
            : '[Auth] Customer Hub BEFH Credit Score Unlock click';
          this.windowRefService.nativeWindow.FS.event(CTA_EVENT);
        }
      // if inactive befh user and clicked cta btn, send to enroll link; otherwise just url from backend
      this.windowRefService.nativeWindow.location.href =
        !kbaCompleted && isCta ? `${url}&next=/financial-health/credit-overview/` : url;
    });
  }

  navigateToCreditCard(card: CardAccount, cardVerificationComponent: ComponentType<any>, trustedAccountUrl?: string) {
    if (trustedAccountUrl) {
      window.open(trustedAccountUrl, '_blank');
      return;
    }

    this.windowRefService.nativeWindow.FS.event('[Auth] Credit Card Login');

    if (card.secondLevelValidated) {
      this.eventTrackingService.trackEvent<LinkClick>(AuthEventType.LINK_CLICK, {
        authClickLinkText: 'Make a payment',
        authClickLinkType: AuthLinkType.NAV_TO_CARD_PAYMENT,
      });
      this.router.navigateByUrl('/redirect/saml2', {
        state: {
          saml: card.saml,
          secondLevelValidated: card.secondLevelValidated,
        },
      });
    } else {
      this.eventTrackingService.trackEnterpriseEvent({
        eventName: 'creditCard2ndLevelVerification',
        cid: this.cookieService.get('cid'),
      });

      this.loadTokenExModal(
        card.lastFourCardNumber,
        this.cookieService.get('X-Access-Token').split('Bearer ')[1],
        cardVerificationComponent
      );
    }
  }

  redirectBefhOnlyUser(befhAccount: BechAccount) {
    this.windowRefService.nativeWindow.FS.event('[Auth] BEFH Only Customer Login');
    this.windowRefService.nativeWindow.location.href = befhAccount.url;
  }

  navigateToBefhCallbackDomain(befhAccount: RedirectAccount | BechAccount, cid: string) {
    this.windowRefService.nativeWindow.FS.event('[Auth] Credit Health Login');
    this.eventTrackingService
      .trackEnterpriseEvent({ eventName: 'redirectToCreditHealthSite', cid: cid })
      .finally(() => {
        this.windowRefService.nativeWindow.location.href = befhAccount.url;
      });
  }

  // passing in cardVerificationComponent to avoid circular dep; we could alternatively move HubCardVerificationModalComponent to the shared module
  loadTokenExModal(lastFourCardNumber: string, idToken: string, cardVerificationComponent: ComponentType<any>) {
    this.dialog.open(cardVerificationComponent, {
      maxWidth: '529px',
      data: { lastFourCardNumber, idToken },
    });
  }
}
